import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import './AdminDashboard.css';
import './ApplicationsList.css';
import AdminLayout from './AdminLayout';

const PORTAL = '/ft-portal';

const AdminSettings = () => {
  const navigate = useNavigate();
  const { admin } = useAuth();
  const [activeTab, setActiveTab] = useState('password');

  // Password change
  const [pwForm, setPwForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [pwLoading, setPwLoading] = useState(false);
  const [pwMsg, setPwMsg] = useState(null);

  // Pricing
  const [pricingForm, setPricingForm] = useState({
    hourly_rate: 37,
    trial_hours: 2,
    trial_price: 70,
    trial_upfront: 40,
    bronze_hours: 10,
    bronze_price: 360,
    bronze_upfront: 100,
    silver_hours: 20,
    silver_price: 710,
    silver_upfront: 150,
    gold_hours: 30,
    gold_price: 1050,
    gold_upfront: 200,
  });
  const [pricingLoading, setPricingLoading] = useState(false);
  const [pricingMsg, setPricingMsg] = useState(null);

  // Load saved pricing from server on mount
  useEffect(() => {
    api.get('/admin/settings/pricing')
      .then(data => {
        if (data?.pricing && Object.keys(data.pricing).length > 0) {
          setPricingForm(prev => ({ ...prev, ...data.pricing }));
        }
      })
      .catch(() => {}); // silently fall back to defaults if table empty
  }, []);

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (pwForm.newPassword !== pwForm.confirmPassword) {
      setPwMsg({ type: 'error', text: 'New passwords do not match.' });
      return;
    }
    if (pwForm.newPassword.length < 8) {
      setPwMsg({ type: 'error', text: 'Password must be at least 8 characters.' });
      return;
    }
    try {
      setPwLoading(true);
      await api.post('/auth/change-password', {
        currentPassword: pwForm.currentPassword,
        newPassword: pwForm.newPassword,
      });
      setPwMsg({ type: 'success', text: 'Password changed successfully.' });
      setPwForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      setPwMsg({ type: 'error', text: err.message || 'Failed to change password.' });
    } finally {
      setPwLoading(false);
    }
  };

  const handlePricingSubmit = async (e) => {
    e.preventDefault();
    try {
      setPricingLoading(true);
      await api.put('/admin/settings/pricing', pricingForm);
      setPricingMsg({ type: 'success', text: 'Pricing updated successfully.' });
    } catch (err) {
      setPricingMsg({ type: 'error', text: err.message || 'Failed to update pricing.' });
    } finally {
      setPricingLoading(false);
    }
  };

  const TABS = [
    { id: 'password', label: '🔒 Change Password' },
    { id: 'pricing', label: '💷 Pricing' },
    { id: 'system', label: '⚙️ System Info' },
  ];

  return (
    <AdminLayout pageTitle="Settings">
      <main className="admin-main" style={{ maxWidth: 760 }}>

        {/* Tab nav */}
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '2rem', borderBottom: '2px solid #e2e8f0', paddingBottom: '0' }}>
          {TABS.map(t => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              style={{
                padding: '0.75rem 1.5rem',
                border: 'none',
                background: 'none',
                fontFamily: 'Montserrat, sans-serif',
                fontWeight: 700,
                fontSize: '0.9rem',
                cursor: 'pointer',
                borderBottom: activeTab === t.id ? '3px solid #CF142B' : '3px solid transparent',
                color: activeTab === t.id ? '#CF142B' : '#64748b',
                marginBottom: '-2px',
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Password tab */}
        {activeTab === 'password' && (
          <div className="recent-section">
            <h2>Change Password</h2>
            {pwMsg && (
              <div className={`alert alert-${pwMsg.type === 'success' ? 'success' : 'error'}`}>
                {pwMsg.type === 'success' ? '✅' : '⚠️'} {pwMsg.text}
              </div>
            )}
            <form onSubmit={handlePasswordChange} style={{ display: 'flex', flexDirection: 'column', gap: '1rem', maxWidth: 420, marginTop: '1rem' }}>
              {[
                { name: 'currentPassword', label: 'Current Password' },
                { name: 'newPassword', label: 'New Password' },
                { name: 'confirmPassword', label: 'Confirm New Password' },
              ].map(field => (
                <div key={field.name}>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: 4, fontSize: '0.9rem' }}>{field.label}</label>
                  <input
                    type="password"
                    value={pwForm[field.name]}
                    onChange={e => setPwForm({ ...pwForm, [field.name]: e.target.value })}
                    required
                    style={{ width: '100%', padding: '0.625rem 0.875rem', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: '0.95rem', boxSizing: 'border-box' }}
                  />
                </div>
              ))}
              <button type="submit" className="btn-approve" disabled={pwLoading} style={{ alignSelf: 'flex-start', marginTop: '0.5rem' }}>
                {pwLoading ? 'Updating...' : 'Update Password'}
              </button>
            </form>
          </div>
        )}

        {/* Pricing tab */}
        {activeTab === 'pricing' && (
          <div className="recent-section">
            <h2>Lesson Package Pricing</h2>
            <p style={{ color: '#64748b', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
              Modify package prices and upfront fees. Changes take effect immediately on the booking page.
            </p>
            {pricingMsg && (
              <div className={`alert alert-${pricingMsg.type === 'success' ? 'success' : 'error'}`}>
                {pricingMsg.type === 'success' ? '✅' : '⚠️'} {pricingMsg.text}
              </div>
            )}
            <form onSubmit={handlePricingSubmit}>
              {/* Hourly rate — full width */}
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: 4, fontSize: '0.85rem', color: '#475569' }}>Standard Hourly Rate (£)</label>
                <input
                  type="number" min="0" step="1"
                  value={pricingForm.hourly_rate}
                  onChange={e => setPricingForm({ ...pricingForm, hourly_rate: Number(e.target.value) })}
                  required
                  style={{ width: '100%', maxWidth: 240, padding: '0.55rem 0.75rem', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: '0.95rem', boxSizing: 'border-box' }}
                />
              </div>

              {/* Package rows — each row: [hours | price | upfront] */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                {[
                  { hours: 'trial_hours', hoursLabel: 'Trial Hours', price: 'trial_price',  priceLabel: 'Trial Price (£)',  upfront: 'trial_upfront',  upfrontLabel: 'Trial Upfront (£)' },
                  { hours: 'bronze_hours', hoursLabel: 'Bronze Hours', price: 'bronze_price', priceLabel: 'Bronze Price (£)', upfront: 'bronze_upfront', upfrontLabel: 'Bronze Upfront (£)' },
                  { hours: 'silver_hours', hoursLabel: 'Silver Hours', price: 'silver_price', priceLabel: 'Silver Price (£)', upfront: 'silver_upfront', upfrontLabel: 'Silver Upfront (£)' },
                  { hours: 'gold_hours', hoursLabel: 'Gold Hours', price: 'gold_price',   priceLabel: 'Gold Price (£)',   upfront: 'gold_upfront',   upfrontLabel: 'Gold Upfront (£)' },
                ].map(pkg => (
                  <React.Fragment key={pkg.price}>
                    <div>
                      <label style={{ display: 'block', fontWeight: 600, marginBottom: 4, fontSize: '0.85rem', color: '#475569' }}>{pkg.hoursLabel}</label>
                      <input
                        type="number" min="0" step="1"
                        value={pricingForm[pkg.hours]}
                        onChange={e => setPricingForm({ ...pricingForm, [pkg.hours]: Number(e.target.value) })}
                        required
                        style={{ width: '100%', padding: '0.55rem 0.75rem', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: '0.95rem', boxSizing: 'border-box' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontWeight: 600, marginBottom: 4, fontSize: '0.85rem', color: '#475569' }}>{pkg.priceLabel}</label>
                      <input
                        type="number" min="0" step="1"
                        value={pricingForm[pkg.price]}
                        onChange={e => setPricingForm({ ...pricingForm, [pkg.price]: Number(e.target.value) })}
                        required
                        style={{ width: '100%', padding: '0.55rem 0.75rem', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: '0.95rem', boxSizing: 'border-box' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontWeight: 600, marginBottom: 4, fontSize: '0.85rem', color: '#475569' }}>{pkg.upfrontLabel}</label>
                      <input
                        type="number" min="0" step="1"
                        value={pricingForm[pkg.upfront]}
                        onChange={e => setPricingForm({ ...pricingForm, [pkg.upfront]: Number(e.target.value) })}
                        required
                        style={{ width: '100%', padding: '0.55rem 0.75rem', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: '0.95rem', boxSizing: 'border-box' }}
                      />
                    </div>
                  </React.Fragment>
                ))}
              </div>
              <button type="submit" className="btn-approve" disabled={pricingLoading}>
                {pricingLoading ? 'Saving...' : '💾 Save Pricing'}
              </button>
            </form>
          </div>
        )}

        {/* System tab */}
        {activeTab === 'system' && (
          <div className="recent-section">
            <h2>System Information</h2>
            <div style={{ display: 'grid', gap: '0.75rem', marginTop: '1rem' }}>
              {[
                { label: 'Admin Email', value: admin?.email || '—' },
                { label: 'Admin Role', value: admin?.role || '—' },
                { label: 'Portal URL', value: '/ft-portal/login' },
                { label: 'API Base', value: process.env.REACT_APP_API_URL || 'http://localhost:5000/api' },
                { label: 'Environment', value: process.env.NODE_ENV || 'development' },
              ].map(row => (
                <div key={row.label} style={{ display: 'flex', gap: '1rem', padding: '0.75rem 1rem', background: '#f8fafc', borderRadius: 8, fontSize: '0.9rem' }}>
                  <span style={{ fontWeight: 700, minWidth: 160, color: '#475569' }}>{row.label}</span>
                  <code style={{ color: '#0A1F4E' }}>{row.value}</code>
                </div>
              ))}
            </div>
          </div>
        )}

      </main>
    </AdminLayout>
  );
};

export default AdminSettings;
